import React, { useCallback } from 'react';
import { API } from '../api';
import PageHeader from '../ui/PageHeader';
import FilterBar from '../ui/FilterBar';
import useUrlFilters from '../ui/useUrlFilters';
import DataTable, { ColumnsMenu, useColumnVisibility } from '../ui/DataTable';
import ListPage, { useListQuery } from '../ui/ListPage';
import ExportButton from './ExportPanel';
import EntityLink from '../ui/EntityLink';
import EmptyState from '../ui/EmptyState';
import Button from '../ui/Button';
import { formatCount } from '../ui/format';
import { packagePath } from '../ui/entityRoutes';

const muted = (text) => <span className="text-graphite">{text}</span>;
const countCell = (n) => (n ? formatCount(n) : muted('0'));

// Keys and values match the URLs the legacy page wrote, so dashboard links and bookmarks keep working.
const FILTER_DEFAULTS = { kw: '', version: '', mode: '', outdated: 'all', multi: '', env: 'all' };

const MODE_REGION = { bsg_only: 'bsg', enterprise_only: 'enterprise' };

const ENV_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'bsg', label: 'BSG' },
  { value: 'enterprise', label: 'Enterprise' },
];

const filtersFor = ({ mode }) => [
  { key: 'kw', type: 'search', label: 'Package', placeholder: 'Name', mono: true, width: 220 },
  { key: 'version', type: 'search', label: 'Version', placeholder: '1.2.3', mono: true, width: 200 },
  {
    key: 'mode',
    type: 'select',
    label: 'Show',
    options: [
      { value: '', label: 'All packages' },
      { value: 'bsg_only', label: 'BSG only' },
      { value: 'enterprise_only', label: 'Enterprise only' },
      { value: 'with_vulns', label: 'With vulnerabilities' },
    ],
  },
  {
    key: 'outdated',
    type: 'select',
    label: 'Version status',
    defaultValue: 'all',
    options: [
      { value: 'all', label: 'All' },
      { value: 'outdated', label: 'Outdated' },
      { value: 'latest', label: 'Latest' },
    ],
  },
  {
    key: 'multi',
    type: 'select',
    label: 'Installed in',
    options: [
      { value: '', label: 'Any environment' },
      { value: '1', label: 'Both environments' },
    ],
  },
  ...(MODE_REGION[mode] ? [] : [
    { key: 'env', type: 'select', label: 'Environment', defaultValue: 'all', options: ENV_OPTIONS },
  ]),
];

const COLUMNS = [
  { key: 'name', header: 'Package', hideable: false, render: (p) => <EntityLink kind="package" id={p.id} name={p.name} /> },
  { key: 'version', header: 'Version', mono: true, maxWidth: 240, fullText: (p) => p.version, render: (p) => p.version },
  { key: 'arch', header: 'Architecture', render: (p) => p.package_arch || muted('—') },
  { key: 'vulns', header: 'Vulnerabilities', align: 'right', render: (p) => countCell(p.vuln_count) },
  { key: 'hosts', header: 'Hosts', align: 'right', render: (p) => countCell(p.host_count) },
  { key: 'source', header: 'Source', mono: true, maxWidth: 180, fullText: (p) => p.source, render: (p) => p.source || muted('—') },
  { key: 'format', header: 'Format', render: (p) => p.format || muted('—') },
  { key: 'vendor', header: 'Vendor', defaultHidden: true, render: (p) => p.vendor || muted('—') },
  { key: 'section', header: 'Section', defaultHidden: true, render: (p) => p.section || muted('—') },
  { key: 'description', header: 'Description', grow: true, minWidth: 200, fullText: (p) => p.description, render: (p) => p.description || muted('No description') },
];

const rowMenu = (p) => [
  { key: 'vulns', label: 'View vulnerabilities', to: packagePath(p, 'vulnerabilities') },
  { key: 'hosts', label: 'View hosts', to: packagePath(p, 'hosts') },
  { key: 'versions', label: 'View other versions', to: packagePath(p, 'versions') },
  { key: 'source', label: 'View packages from the same source', to: packagePath(p, 'source') },
];

// API parameters for the current filters, as the legacy page sent them.
const apiParams = (filters) => ({
  region: MODE_REGION[filters.mode] || filters.env,
  outdated: filters.outdated === 'outdated' || filters.outdated === 'latest' ? filters.outdated : undefined,
  multi: filters.multi ? 'multi' : undefined,
  with_vulns: filters.mode === 'with_vulns' ? 'true' : undefined,
  kw: filters.kw,
  version: filters.version,
});

export default function PackagesPage() {
  const [filters, setFilters] = useUrlFilters(FILTER_DEFAULTS);
  const columnState = useColumnVisibility(COLUMNS, 'sbom-columns-packages');

  const params = apiParams(filters);
  const key = JSON.stringify(params);
  const fetchPage = useCallback(
    (paging) => API.GET_PACKAGES({ ...params, ...paging }),
    [key], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const query = useListQuery(fetchPage, key, 'packages');

  // The mode's BSG and Enterprise options set the environment; the other modes reset it.
  const changeFilters = (next) => {
    const env = next.mode === filters.mode ? next.env : MODE_REGION[next.mode] || 'all';
    setFilters({ ...next, env });
  };

  const filterDefs = filtersFor({ mode: filters.mode });
  const filtered = filterDefs.some((f) => (filters[f.key] ?? '') !== (f.defaultValue ?? ''));
  const empty = filtered ? (
    <EmptyState title="No packages match these filters" action={<Button onClick={() => changeFilters(FILTER_DEFAULTS)}>Clear filters</Button>}>
      Package and version match part of the name or version string. Widen the other filters, or clear them.
    </EmptyState>
  ) : (
    <EmptyState title="No packages found">
      Packages appear here once hosts report their inventory.
    </EmptyState>
  );

  return (
    <ListPage>
      <PageHeader
        title="Packages"
        actions={(
          <>
            <ColumnsMenu columns={COLUMNS} hidden={columnState.hidden} onToggle={columnState.toggle} onReset={columnState.reset} />
            <ExportButton env={params.region} />
          </>
        )}
      />
      <FilterBar className="mb-3" filters={filterDefs} values={filters} onChange={changeFilters} />
      <DataTable
        className="flex-1"
        label="Packages"
        columns={COLUMNS}
        hidden={columnState.hidden}
        rows={query.rows}
        rowKey={(p) => p.id}
        rowLabel={(p) => `${p.name} ${p.version}`}
        rowMenu={rowMenu}
        loading={query.loading}
        error={query.error}
        errorTitle="Packages could not be loaded"
        onRetry={query.retry}
        empty={empty}
        pagination={query.pagination}
      />
    </ListPage>
  );
}
